// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, User, KeyRound, ShieldAlert, Eye, EyeOff } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = {
  admin: 'Administrador',
  servo_contas: 'Servo de Contas',
  secretario: 'Secretário',
  auditor: 'Auditor',
  membro: 'Membro',
};

const PERMISSION_LABELS = {
  congregacao: 'Contas da Congregação',
  manutencao: 'Contas da Manutenção',
};

export default function MyAccountPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [saving, setSaving] = useState(false);

  const role = user?.is_admin ? 'admin' : (user?.role || 'membro');

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error('A nova senha deve ter pelo menos 8 caracteres');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('A confirmação não confere com a nova senha');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success('Senha alterada com sucesso! Entre novamente com a nova senha.');
      setTimeout(() => {
        logout();
        navigate('/');
      }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao alterar senha');
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-4 sm:p-8">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            data-testid="my-account-back-button"
            className="rounded-xl"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-bold text-foreground">Minha Conta</h1>
        </div>

        {user?.must_change_password && (
          <div
            className="flex items-start gap-3 p-4 rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800"
            data-testid="must-change-password-alert"
          >
            <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-800 dark:text-amber-200">
              Sua senha foi redefinida por um administrador. Por segurança,
              defina uma nova senha pessoal abaixo.
            </p>
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="w-5 h-5 text-primary" />
              Meus Dados
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3" data-testid="my-account-info">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-muted-foreground">Nome</p>
                <p className="font-medium text-foreground">{user?.name}</p>
              </div>
              <div>
                <p className="text-muted-foreground">E-mail</p>
                <p className="font-medium text-foreground">{user?.email}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Papel</p>
                <Badge variant="secondary">{ROLE_LABELS[role] || role}</Badge>
              </div>
              <div>
                <p className="text-muted-foreground">Áreas com acesso</p>
                <div className="flex flex-wrap gap-1.5 mt-0.5">
                  {user?.is_admin ? (
                    <Badge>Todas (admin)</Badge>
                  ) : (user?.permissions || []).length > 0 ? (
                    user.permissions.map((p) => (
                      <Badge key={p} variant="outline">{PERMISSION_LABELS[p] || p}</Badge>
                    ))
                  ) : (
                    <span className="text-xs text-muted-foreground">Nenhuma área liberada</span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <KeyRound className="w-5 h-5 text-primary" />
              Alterar Senha
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="current-password">Senha atual</Label>
                <Input
                  id="current-password"
                  type={showPasswords ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  data-testid="current-password-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="new-password">Nova senha (mínimo 8 caracteres)</Label>
                <Input
                  id="new-password"
                  type={showPasswords ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  data-testid="new-password-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-password">Confirmar nova senha</Label>
                <Input
                  id="confirm-password"
                  type={showPasswords ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  data-testid="confirm-password-input"
                />
              </div>
              <button
                type="button"
                onClick={() => setShowPasswords((s) => !s)}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                data-testid="toggle-password-visibility"
              >
                {showPasswords ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                {showPasswords ? 'Ocultar senhas' : 'Mostrar senhas'}
              </button>
              <p className="text-xs text-muted-foreground">
                Após alterar a senha, você será desconectado e precisará entrar novamente.
              </p>
              <Button type="submit" disabled={saving} className="w-full sm:w-auto" data-testid="change-password-submit">
                {saving ? 'Alterando...' : 'Alterar Senha'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
